import React, { useEffect, useRef, useState } from 'react';
import { FaFilter, FaTimes, FaSearch, FaSortAmountDownAlt, FaSortAmountUpAlt, FaCalendarAlt, FaPercent } from 'react-icons/fa';
import { ScreenerSortDir, ScreenerStockFilters } from '../../services/screenerApi.ts';
import './ScreenerFilterBar.css';

interface ScreenerFilterBarProps {
    onApply: (filters: ScreenerStockFilters) => void;
}

const ScreenerFilterBar: React.FC<ScreenerFilterBarProps> = ({ onApply }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [symbolInput, setSymbolInput] = useState('');
    const [symbols, setSymbols] = useState<string[]>([]);
    const [sortEnabled, setSortEnabled] = useState(false);
    const [sortDir, setSortDir] = useState<ScreenerSortDir>('desc');
    const [capturedFrom, setCapturedFrom] = useState('');
    const [capturedTo, setCapturedTo] = useState('');
    const [minChangeInput, setMinChangeInput] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const addSymbol = () => {
        const value = symbolInput.trim().toUpperCase();
        if (value && !symbols.includes(value)) {
            setSymbols(prev => [...prev, value]);
        }
        setSymbolInput('');
    };

    const handleSymbolKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addSymbol();
        }
    };

    const removeSymbol = (symbol: string) => {
        setSymbols(prev => prev.filter(s => s !== symbol));
    };

    const handleMinChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Allow empty, an optional leading minus sign, digits and at most one decimal point
        if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
            setMinChangeInput(value);
        }
    };

    const handleApply = () => {
        const trimmedMinChange = minChangeInput.trim();
        onApply({
            symbols,
            capturedFrom: capturedFrom || undefined,
            capturedTo: capturedTo || undefined,
            minLatestChangePct: trimmedMinChange !== '' ? Number(trimmedMinChange) : undefined,
            sortBy: sortEnabled ? 'maxUpside' : undefined,
            sortDir: sortEnabled ? sortDir : undefined,
        });
        setIsOpen(false);
    };

    const handleClear = () => {
        setSymbols([]);
        setSymbolInput('');
        setSortEnabled(false);
        setSortDir('desc');
        setCapturedFrom('');
        setCapturedTo('');
        setMinChangeInput('');
        onApply({});
        setIsOpen(false);
    };

    const activeCount = symbols.length + (sortEnabled ? 1 : 0) + (capturedFrom || capturedTo ? 1 : 0)
        + (minChangeInput.trim() !== '' ? 1 : 0);

    return (
        <div className="screener-filter-bar" ref={containerRef}>
            <button
                type="button"
                className={`screener-filter-btn ${activeCount > 0 ? 'active' : ''}`}
                onClick={() => setIsOpen(o => !o)}
            >
                <FaFilter />
                <span>Filters</span>
                {activeCount > 0 && <span className="screener-filter-badge">{activeCount}</span>}
            </button>

            {isOpen && (
                <>
                <div className="screener-filter-backdrop" onClick={() => setIsOpen(false)} />
                <div className="screener-filter-panel">
                    <div className="screener-filter-header">
                        <span className="screener-filter-title">
                            <FaFilter /> Filters
                        </span>
                        <button
                            type="button"
                            className="screener-filter-close"
                            onClick={() => setIsOpen(false)}
                            aria-label="Close filters"
                        >
                            <FaTimes />
                        </button>
                    </div>

                    <div className="screener-filter-body">
                        <div className="screener-filter-section">
                            <label className="screener-filter-label">Stock Symbols</label>
                            <div className="screener-filter-input-wrap">
                                <FaSearch className="screener-filter-input-icon" />
                                <input
                                    type="text"
                                    className="screener-filter-input"
                                    placeholder="Type symbol & press Enter (e.g. TCS)"
                                    value={symbolInput}
                                    onChange={(e) => setSymbolInput(e.target.value)}
                                    onKeyDown={handleSymbolKeyDown}
                                />
                            </div>
                            {symbols.length > 0 && (
                                <div className="screener-filter-chips">
                                    {symbols.map(symbol => (
                                        <span className="screener-filter-chip" key={symbol}>
                                            {symbol}
                                            <button
                                                type="button"
                                                onClick={() => removeSymbol(symbol)}
                                                aria-label={`Remove ${symbol}`}
                                            >
                                                <FaTimes />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="screener-filter-divider" />

                        <div className="screener-filter-section">
                            <label className="screener-filter-label">Captured Date Range</label>
                            <div className="screener-filter-date-row">
                                <div className="screener-filter-date-field">
                                    <span className="screener-filter-date-tag">From</span>
                                    <div className="screener-filter-input-wrap">
                                        <FaCalendarAlt className="screener-filter-input-icon" />
                                        <input
                                            type="date"
                                            className="screener-filter-input screener-filter-date-input"
                                            value={capturedFrom}
                                            onChange={(e) => setCapturedFrom(e.target.value)}
                                            max={capturedTo || undefined}
                                            aria-label="Captured from date"
                                        />
                                    </div>
                                </div>
                                <div className="screener-filter-date-field">
                                    <span className="screener-filter-date-tag">To</span>
                                    <div className="screener-filter-input-wrap">
                                        <FaCalendarAlt className="screener-filter-input-icon" />
                                        <input
                                            type="date"
                                            className="screener-filter-input screener-filter-date-input"
                                            value={capturedTo}
                                            onChange={(e) => setCapturedTo(e.target.value)}
                                            min={capturedFrom || undefined}
                                            aria-label="Captured to date"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="screener-filter-divider" />

                        <div className="screener-filter-section">
                            <label className="screener-filter-label">Latest Candle Move %</label>
                            <div className="screener-filter-input-wrap">
                                <FaPercent className="screener-filter-input-icon" />
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    className="screener-filter-input"
                                    placeholder="e.g. 5 for +5% or more"
                                    value={minChangeInput}
                                    onChange={handleMinChangeInput}
                                />
                            </div>
                            <p className="screener-filter-hint">
                                Shows stocks whose latest candle moved at least this much (use a negative number for declines).
                            </p>
                        </div>

                        <div className="screener-filter-divider" />

                        <div className="screener-filter-section">
                            <label className="screener-filter-switch-label">
                                <span>Sort by Max Upside %</span>
                                <span className={`screener-filter-switch ${sortEnabled ? 'on' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={sortEnabled}
                                        onChange={(e) => setSortEnabled(e.target.checked)}
                                    />
                                    <span className="screener-filter-switch-knob" />
                                </span>
                            </label>
                            {sortEnabled && (
                                <div className="screener-filter-sort-options">
                                    <button
                                        type="button"
                                        className={`screener-sort-btn ${sortDir === 'desc' ? 'active' : ''}`}
                                        onClick={() => setSortDir('desc')}
                                    >
                                        <FaSortAmountDownAlt /> High to Low
                                    </button>
                                    <button
                                        type="button"
                                        className={`screener-sort-btn ${sortDir === 'asc' ? 'active' : ''}`}
                                        onClick={() => setSortDir('asc')}
                                    >
                                        <FaSortAmountUpAlt /> Low to High
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="screener-filter-actions">
                        <button type="button" className="screener-filter-clear" onClick={handleClear}>
                            Clear All
                        </button>
                        <button type="button" className="screener-filter-apply" onClick={handleApply}>
                            Apply Filters
                        </button>
                    </div>
                </div>
                </>
            )}
        </div>
    );
};

export default ScreenerFilterBar;
